import { Factory } from 'fishery'
import random from 'lodash/random'
import { OpalApiComponent } from '@opallabs/api'
import { linkageFromResource } from '@opallabs/json-api'
import { seedData } from '../../../Users/seed_data'
import { userProfileResourceFactory } from './user_profile'
import { workspaceResourceFactory } from './workspace'

type UserResourceV3 = OpalApiComponent<'V3', 'user'>

// Each build takes the next seed user after a random start, so a list of up to
// `seedData.length` users has no repeated ids.
const firstSeedIndex = random(seedData.length - 1)

export const userResourceFactory: Factory<UserResourceV3> =
  Factory.define<UserResourceV3>(({ params, afterBuild, sequence }) => {
    afterBuild((user) => {
      /**
       * The `profile` resource circularly depends on this `user` existing. To work around this, we will
       * stub a fake profile originally, then create and set an actual `profile` resource once the user has
       * been built and can be used.
       *
       * This won't run if a profile relationship is created beforehand and passed in as a parameter
       */
      if (!params.relationships?.profile) {
        const userProfile = userProfileResourceFactory.build({
          relationships: { user: linkageFromResource(user) },
        })
        user.relationships.profile = linkageFromResource(userProfile)
      }
    })

    const { id, ...seed } =
      seedData[(firstSeedIndex + sequence) % seedData.length]
    const full_name = `${seed.first_name} ${seed.last_name}`

    return {
      id,
      type: 'user',
      attributes: {
        ...seed,
        created_at: new Date().toISOString(),
        feature_flags: undefined,
        full_name,
        is_active: true,
        role_name: undefined,
        title: null,
        // Not accurate--this field is meant to be the output of the `i18n` gem's `I18n.transliterate` method--
        // but we never use it so accuracy is irrelevant.
        //
        // @see https://www.rubydoc.info/gems/activesupport/2.3.17/I18n.transliterate
        transliterated_full_name: full_name,
        updated_at: new Date().toISOString(),
      },

      relationships: {
        profile: {
          data: { id: 'stubbed_until_afterBuild', type: 'user_profile' },
        },
        workspaces: linkageFromResource([workspaceResourceFactory.build()]),
      },
      links: {
        v2_request: `/users/v2/${seed.legacy_id}`,
      },
    } satisfies UserResourceV3
  })
